import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '../../context/DashboardContext';
import { usePortfolioView } from '../../context/PortfolioViewContext';
import { selectInvestmentsTable } from '../../state/dashboardSelectors';
import type { TimeWindow } from '../../services/dashboardApi';
import { getTickerMarketBatch } from '../../services/tickerApi';
import { useAuth } from '../../../auth/hooks/useAuth';
import { ShowRealizedGainsToggle } from './ShowRealizedGainsToggle';

/**
 * InvestmentTableCard
 * 
 * Pure display component for the investment table.
 * Shows expandable accounts with their tickers, shares, and performance metrics.
 * 
 * This is a read-only display component - all business logic and state management
 * is handled by the parent component (PortfolioPage).
 */

/**
 * Formats share values with special handling for small values (< 0.1).
 * For small values, shows the first non-zero decimal digit (up to 6 decimals).
 * For larger values, shows as integer or 1 decimal place (removing trailing .0).
 * 
 * Examples:
 * - 0.012 -> 0.01
 * - 0.0049 -> 0.005
 * - 0.0000032 -> 0.000003
 * - 1000 -> 1,000
 * - 102434.8 -> 102,434.8
 * - 1.5 -> 1.5
 * - 1.0 -> 1
 */
function formatShares(shares: number | null): string {
  if (shares == null) {
    return '—';
  }

  // For small values (< 0.1), find first non-zero decimal and round to that place
  if (shares > 0 && shares < 0.1) {
    // Use toFixed(6) to avoid scientific notation, then find first non-zero digit
    const str = shares.toFixed(6);
    const decimalIndex = str.indexOf('.');
    
    if (decimalIndex !== -1) {
      // Find first non-zero digit after decimal point (up to 6 decimals)
      for (let i = decimalIndex + 1; i < str.length; i++) {
        if (str[i] !== '0') {
          // Found first non-zero digit at position i
          // Round to (i - decimalIndex) decimal places
          const decimals = i - decimalIndex;
          const rounded = shares.toFixed(decimals);
          // Remove trailing zeros but keep at least one digit after decimal
          return rounded.replace(/\.?0+$/, '') || '0';
        }
      }
    }
    // Fallback: if all decimals are zero (shouldn't happen for < 0.1), return 0
    return '0';
  }

  // For larger values: integer or 1 decimal (remove trailing .0)
  if (Number.isInteger(shares)) {
    return shares.toLocaleString('en-US');
  }
  
  return shares.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
}

export interface InvestmentTableCardProps {
  // Selection state
  selectedTicker: string | null;
  selectedAccountId: string | null;
  expandedAccounts: Set<string>;
  
  // Handlers
  onSelectAccountTicker: (accountId: string, ticker: string) => void;
  onSelectAccount: (accountId: string) => void;
  onToggleAccountExpansion: (accountId: string) => void;
  onEditPortfolio: () => void;
  
  // Hide shares column for friend portfolio views
  hideShares?: boolean;
  
  // Show "My Total" text in header (hide text but keep column structure for Silver tier)
  showMyTotalText?: boolean;
  
  // Hide "Show realized gains" checkbox for friend portfolio views (Tier 2/3)
  hideShowRealizedGains?: boolean;

  // Disable account expansion (used for friend views without ticker visibility)
  enableAccountExpansion?: boolean;

  // Hide Edit button for friend portfolio views
  hideEditButton?: boolean;

  className?: string;
}

export const InvestmentTableCard: React.FC<InvestmentTableCardProps> = ({
  selectedTicker,
  selectedAccountId,
  expandedAccounts,
  onSelectAccountTicker,
  onSelectAccount,
  onToggleAccountExpansion,
  onEditPortfolio,
  hideShares = false,
  showMyTotalText = true,
  hideShowRealizedGains = false,
  enableAccountExpansion = true,
  hideEditButton = false,
  className = '',
}) => {
  const { data, isFetching } = useDashboard();
  const { selectedTimeWindow, showRealizedGains } = usePortfolioView();
  const { session } = useAuth();
  
  // Get top mover ticker symbols for the current window
  // Structure: topMovers[window] = {tickers: [...]}
  const topMoverTickers = useMemo(() => {
    if (!data?.topMovers) return [];
    const topMovers = data.topMovers;
    const windowData = topMovers[selectedTimeWindow.key as TimeWindow];
    const tickerSymbols = windowData?.tickers;
    return Array.isArray(tickerSymbols) ? tickerSymbols : [];
  }, [data, selectedTimeWindow.key]);
  
  // Fetch ticker market data to populate tickerWindows (for % change per time window)
  const { data: tickerMarketData } = useQuery({
    queryKey: ['ticker-market-batch-investment-table', topMoverTickers, selectedTimeWindow.key],
    queryFn: async () => {
      if (!session?.access_token || topMoverTickers.length === 0) {
        return null;
      }
      return getTickerMarketBatch({
        tickers: topMoverTickers,
        token: session.access_token,
      });
    },
    enabled: topMoverTickers.length > 0 && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend TTL)
  });
  
  // Enrich dashboard data with tickerWindows from market data
  const enrichedData = useMemo(() => {
    if (!data || !tickerMarketData?.data) return data;
    
    // Check if it's a batch response (has tickers property)
    const batchData = tickerMarketData.data as any;
    if (!batchData.tickers || typeof batchData.tickers !== 'object') return data;
    
    // Build tickerWindows structure from history_percentage
    // Format: { [ticker]: { [window]: pct } }
    const tickerWindows: Record<string, Record<string, number>> = {};
    Object.entries(batchData.tickers).forEach(([ticker, marketInfo]: [string, any]) => {
      if (marketInfo.history_percentage && typeof marketInfo.history_percentage === 'object') {
        tickerWindows[ticker.toUpperCase()] = marketInfo.history_percentage;
      }
    });
    
    // Merge tickerWindows into data
    return {
      ...data,
      tickerWindows,
    };
  }, [data, tickerMarketData]);
  
  const investmentsSnapshot = useMemo(
    () => selectInvestmentsTable(enrichedData, selectedTimeWindow.key as TimeWindow),
    [enrichedData, selectedTimeWindow.key]
  );
  
  const timeWindowLabel = selectedTimeWindow.label;
  
  // Responsive shares hiding - hide when viewport is squeezed (between 768px and 1030px)
  const [hideSharesResponsive, setHideSharesResponsive] = useState(false);

  // Responsive shares column hiding - based on viewport width
  useEffect(() => {
    const handleResize = () => {
      const viewportWidth = window.innerWidth;
      // Hide Shares when viewport is between 768px and 1030px (inclusive)
      setHideSharesResponsive(viewportWidth >= 768 && viewportWidth <= 1030);
    };

    // Run once on mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Combined hideShares logic (prop + responsive)
  const shouldHideShares = hideShares || hideSharesResponsive;

  // Filter tickers based on checkbox state
  const filteredInvestmentsSnapshot = useMemo(() => {
    if (showRealizedGains) {
      // Show all tickers, including realized gains/zero-share rows
      return investmentsSnapshot;
    }

    const hasVisiblePosition = (ticker: (typeof investmentsSnapshot.accounts)[number]['tickers'][number]) => {
      const hasShares = ticker.shares !== null && ticker.shares !== 0;
      const hasMyTotal = typeof ticker.myTotalPct === 'number';
      const hasNA = ticker.myTotalPct === 'NA'; // Non-ticker with shares (always show)
      return hasShares || hasMyTotal || hasNA;
    };

    return {
      accounts: investmentsSnapshot.accounts.map((account) => ({
        ...account,
        tickers: account.tickers.filter(hasVisiblePosition),
      })),
    };
  }, [investmentsSnapshot, showRealizedGains]);

  return (
    <div className={`bg-white rounded-lg p-6 shadow-sm flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-green-700">Investments</h2>
          {/* Edit Button - Hidden for friend portfolio views */}
          {!hideEditButton && (
            <button
              onClick={onEditPortfolio}
              className="p-1 rounded-full bg-green-100 text-green-600 hover:bg-green-200"
              title="Add or Edit Accounts"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          )}
        </div>
        {/* Show Realized Gains Checkbox - Hidden for friend views (Tier 2/3) */}
        {!hideShowRealizedGains && (
          <ShowRealizedGainsToggle />
        )}
      </div>

      {/* Table Container with Scroll */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <table className="table-fixed w-full text-sm text-left">
          <colgroup>
            {/* 25% for account name (or 33.33% if shares hidden) */}
            <col style={{ width: shouldHideShares ? '33.33%' : '25%' }} />
            {/* 25% for shares (hidden on xs or if shouldHideShares) */}
            {!shouldHideShares && <col className="hidden sm:table-cell" style={{ width: '25%' }} />}
            {/* 25% for performance (or 33.33% if shares hidden) */}
            <col style={{ width: shouldHideShares ? '33.33%' : '25%' }} />
            {/* 25% for My total (or 33.33% if shares hidden) */}
            <col style={{ width: shouldHideShares ? '33.33%' : '25%' }} />
          </colgroup>

          <thead className="text-xs text-gray-500 uppercase sticky top-0 bg-white z-10">
            <tr>
              <th scope="col" className="py-3 px-1 pl-3 font-medium normal-case">
                Account
              </th>
              {!shouldHideShares && (
                <th
                  scope="col"
                  className="py-3 px-1 text-center font-medium normal-case hidden sm:table-cell"
                >
                  Shares
                </th>
              )}
              <th
                scope="col"
                className="py-3 px-1 pr-2 text-right font-medium normal-case whitespace-nowrap"
              >
                {showMyTotalText ? 'My total' : ''}
              </th>
              <th
                scope="col"
                className="py-3 px-1 pr-2 text-right text-blue-500 font-medium normal-case whitespace-nowrap"
              >
                {timeWindowLabel}
              </th>
            </tr>
          </thead>

          <tbody>
            {isFetching && filteredInvestmentsSnapshot.accounts.length === 0 ? (
              <tr>
                <td colSpan={shouldHideShares ? 3 : 4} className="text-center py-8">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                    <span className="text-sm text-gray-500">Loading data...</span>
                  </div>
                </td>
              </tr>
            ) : filteredInvestmentsSnapshot.accounts.length === 0 ? (
              <tr>
                <td colSpan={shouldHideShares ? 3 : 4} className="text-center py-4 text-gray-500">
                  No investment accounts added yet.
                </td>
              </tr>
            ) : null}

            {filteredInvestmentsSnapshot.accounts.map((accRow) => {
              const canExpandAccount = enableAccountExpansion;
              const isExpanded = canExpandAccount && expandedAccounts.has(accRow.accountId);
              const isHidden = accRow.isHidden ?? false;

              return (
                <React.Fragment key={accRow.accountId}>
                  {/* Account Row */}
                  <tr
                    className={`border-b dark:border-gray-700 cursor-pointer ${
                      selectedAccountId === accRow.accountId && !selectedTicker
                        ? 'bg-gray-200 text-gray-800 rounded-lg'
                        : 'bg-white text-gray-700 hover:bg-gray-50 rounded-lg'
                    }`}
                    onClick={() => onSelectAccount(accRow.accountId)}
                  >
                    {/* Account Name & Logo - 85% width */}
                    <td 
                      className="py-4 pl-3 pr-1 whitespace-nowrap"
                      colSpan={shouldHideShares ? 2 : 3}
                      style={{ width: '85%' }}
                    >
                      <div className="flex items-center">
                        {/* Expand/Collapse Triangle Button */}
                        {canExpandAccount && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              onToggleAccountExpansion(accRow.accountId);
                            }}
                            className={`flex-shrink-0 p-1 mr-1 transition-colors ${
                              isHidden ? 'text-gray-300 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                            }`}
                            aria-label={isExpanded ? 'Collapse account' : 'Expand account'}
                          >
                            <svg
                              className={`w-4 h-4 transition-transform duration-200 ${
                                isExpanded ? 'rotate-90' : 'rotate-0'
                              }`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        )}
                        <div className="relative flex-shrink-0 h-8 w-8">
                          <img
                            className={`h-8 w-8 rounded-full ${isHidden ? 'opacity-50' : ''}`}
                            src={accRow.logoUrl || '/default-logo.png'}
                            alt=""
                          />
                          {accRow.isSnapTradeLinked && (
                            <span
                              className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white shadow-sm"
                              title="Linked external account"
                              aria-label="Linked external account"
                            >
                              <span
                                className="block h-3 w-3 bg-green-600"
                                style={{
                                  WebkitMask: "url('/icon-link-lrg.png') center / contain no-repeat",
                                  mask: "url('/icon-link-lrg.png') center / contain no-repeat",
                                }}
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </div>
                        <div className="ml-3 min-w-0 flex-1">
                          <div 
                            className={`text-sm font-medium truncate ${isHidden ? 'text-gray-400' : ''}`}
                            title={accRow.accountName || accRow.accountId}
                          >
                            {accRow.accountName || accRow.accountId}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* My Total (account-level aggregate - align with ticker My total) - 15% width */}
                    <td 
                      className="py-4 pl-1 pr-2 text-right whitespace-nowrap"
                      style={{ width: '15%' }}
                    >
                      {(() => {
                        const raw = accRow.accountPct;
                        if (typeof raw !== 'number')
                          return <span className="text-gray-400 text-sm">—</span>;
                        const sign = raw >= 0 ? '+' : '';
                        const color = raw >= 0 ? 'text-green-600' : 'text-red-600';
                        return (
                          <span className={`text-sm font-medium ${color}`}>
                            {sign}
                            {raw.toFixed(1)}%
                          </span>
                        );
                      })()}
                    </td>
                  </tr>

                  {/* Expanded Row - Display Tickers */}
                  {canExpandAccount && isExpanded && (
                    <tr className="bg-white border-b">
                      <td colSpan={shouldHideShares ? 3 : 4} className="p-0 bg-white">
                        <div className="px-1 py-2 bg-white">
                          {accRow.tickers && accRow.tickers.length > 0 ? (
                            <div className="space-y-1">
                              {accRow.tickers.map((row) => {
                                const isActive =
                                  row.symbol === selectedTicker &&
                                  accRow.accountId === selectedAccountId;
                                const isTickerHidden = accRow.hiddenTickers?.includes(row.symbol) ?? false;

                                return (
                                  <button
                                    key={row.symbol}
                                    onClick={() =>
                                      onSelectAccountTicker(accRow.accountId, row.symbol)
                                    }
                                    aria-pressed={isActive}
                                    className={`
                                      w-full grid ${shouldHideShares ? 'grid-cols-3' : 'grid-cols-4'} gap-x-2 items-center 
                                      text-xs 
                                      border-t border-gray-100 first:border-t-0 pt-1 first:pt-0 
                                      py-1 px-1 rounded-lg
                                      ${isActive ? 'bg-gray-200' : 'bg-white hover:bg-gray-50'}
                                      text-left
                                    `}
                                  >
                                    {/* Column 1: Ticker */}
                                    <div className={`col-span-1 font-medium truncate pl-1 ${isTickerHidden ? 'text-gray-400' : 'text-gray-700'}`}>
                                      {row.symbol}
                                    </div>

                                    {/* Column 2: Shares (from cache) - Hidden for friend portfolio or when squeezed */}
                                    {!shouldHideShares && (
                                      <div className="col-span-1 text-center text-gray-600 px-1 hidden sm:block">
                                        {formatShares(row.shares)}
                                      </div>
                                    )}

                                    {/* Column 3: My Total % (from cache) */}
                                    <div className="col-span-1 text-right pl-1 pr-2">
                                      {(() => {
                                        const mt = row.myTotalPct;
                                        if (mt === 'NA') {
                                          // Non-ticker with shares - show dash (same as None)
                                          return (
                                            <span className="text-xs text-gray-400">—</span>
                                          );
                                        }
                                        if (typeof mt !== 'number') {
                                          // None or other non-number - show dash
                                          return (
                                            <span className="text-xs text-gray-400">—</span>
                                          );
                                        }
                                        // Number - show percentage with sign and color
                                        const sign = mt >= 0 ? '+' : '';
                                        const cls = mt >= 0 ? 'text-green-600' : 'text-red-600';
                                        return (
                                          <span className={`text-xs ${cls}`}>{`${sign}${mt.toFixed(
                                            1
                                          )}%`}</span>
                                        );
                                      })()}
                                    </div>

                                    {/* Column 4: Time Window % (from global tickerWindows) */}
                                    <div className="col-span-1 text-right pl-1 pr-2">
                                      {(() => {
                                        // Check if this is a non-ticker (exchange === 'MANUAL')
                                        const isNonTicker = row.exchange === 'MANUAL';
                                        const raw = row.tickerPct;
                                        
                                        // For non-tickers, always show dash (even if tickerPct is a number)
                                        if (isNonTicker || typeof raw !== 'number') {
                                          return <span className="text-xs text-gray-400">—</span>;
                                        }
                                        
                                        // Regular ticker with valid percentage
                                        const sign = raw >= 0 ? '+' : '';
                                        const cls = raw >= 0 ? 'text-green-600' : 'text-red-600';
                                        return (
                                          <span className={`text-xs ${cls}`}>{`${sign}${raw.toFixed(
                                            1
                                          )}%`}</span>
                                        );
                                      })()}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 pl-1 py-1">
                              No holdings in this account.
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
